import { coerce } from "./helpers"

const FLUSH_EVERY = 5_000
// UsageChannel keeps at most this many events per perform (UsageEvents::MAX_EVENTS)
// and drops the rest, so a backlog goes over in several performs.
const BATCH_SIZE = 50
// At most this many events wait per flush window. Past it the newest are
// dropped (the queue keeps what it has) and counted; the next flush leads
// with one events_dropped event. Also bounds a tab whose socket never connects.
const MAX_QUEUE = 100

// Buffered product-usage events, sent in batches over UsageChannel. Module
// scope survives Turbo visits (application.js loads once), so one
// subscription serves the whole session. Events fired before the socket
// connects wait in the queue and flush on connect.
let queue = []
let timer = null
let subscription = null
let connected = false
let dropped = 0

// Path with id segments (and their slugs, 12-some-org) blanked, so
// "which page/form" has bounded cardinality. Extensions survive: /plans/:id.docx
export const blankIds = path => path.replace(/\/\d+[^/.]*/g, "/:id")

// Not MetaVars: it logs every miss in development, and most pages have no
// plan (or organization) to miss.
const meta = name => {
  const tag = document.querySelector(`meta[name='${name}']`)
  return tag ? coerce(tag.content) : null
}

export function track(name, props = {}) {
  // A masquerading super admin is not the user whose session this is.
  if (!meta("userId") || meta("userMasquerading")) return
  if (queue.length >= MAX_QUEUE) { dropped++; return }

  queue.push(envelope(name, props))
  subscribe()
  timer ??= setTimeout(flush, FLUSH_EVERY)
}

// The page context every event carries, read when the event fires: a Turbo
// visit swaps the meta tags, and a queued event belongs to the page it came from.
const envelope = (name, props) => ({
  name,
  props,
  organization_id: meta("organizationId"),
  plan_id: meta("planId"),
  path: location.pathname,
  t: Date.now()
})

export function flush() {
  clearTimeout(timer)
  timer = null
  if (!connected || queue.length === 0) return

  if (dropped > 0) {
    queue.unshift(envelope("events_dropped", { count: dropped, where: "client" }))
    dropped = 0
  }
  while (queue.length > 0) {
    const batch = queue.splice(0, BATCH_SIZE)
    // perform() returns false when the socket is not open — `connected` lags
    // a dropped socket (pagehide, a flush right after the server went away).
    // Put the batch back for the reconnect instead of losing it.
    if (!subscription.perform("track", { events: batch })) {
      queue.unshift(...batch)
      break
    }
  }
}

async function subscribe() {
  if (subscription) return

  subscription = {}
  try {
    // Imported on first use so controllers that only call track() load without Turbo.
    const { cable } = await import("@hotwired/turbo-rails")
    const consumer = await cable.getConsumer()
    subscription = consumer.subscriptions.create(
      { channel: "UsageChannel" },
      { connected() { connected = true; flush() }, disconnected() { connected = false } }
    )
  } catch {
    // Leave the placeholder behind so the next track() tries again.
    subscription = null
  }
}

window.addEventListener("pagehide", flush)
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush() })
